import { useCallback } from 'react';
import moment from 'moment';
import { usePromptsContext } from 'context/prompts/PromptsContext';
import { useUserSubscription } from 'components/Account/operation.generated';
import { useProductUsageQuery } from 'components/ProductUsage/operation.generated';
import { useFeatureFlags } from 'components/FeatureFlags';
import {
  getDownloadMinutesUsage,
  getProductUsageAccessScope,
} from 'components/Account/Usage/downloadMinutesUtils';

// DownloadLimitReachedModal is only for metered (add-ons) accounts.
const useDownloadLimitPrompt = () => {
  const { isFeatureEnabled } = useFeatureFlags();
  const { data: usageData } = useProductUsageQuery();
  const { data: subscriptionData } = useUserSubscription();
  const subscription = subscriptionData?.user.subscription;
  const [, dispatchPrompt] = usePromptsContext();

  const downloadLimitPrompt = useCallback(
    (durationMs: number) => {
      if (!isFeatureEnabled('add-ons') || !usageData) {
        return false;
      }

      const { productAccessLimits, productAccessPeriodUsage } =
        getProductUsageAccessScope(usageData.user);

      const usage = getDownloadMinutesUsage(
        productAccessLimits,
        productAccessPeriodUsage
      );

      if (!usage || usage.remainingMs >= durationMs) {
        return false;
      }

      if (subscription) {
        dispatchPrompt({
          type: 'OpenDownloadLimitReachedPrompt',
          payload: {
            subscription,
            remainingMinutes: usage.remainingMinutes,
            limitMinutes: usage.limitMinutes,
            resetDate: moment.unix(usage.periodEnd).format('MMMM D'),
            durationMinutes: Math.ceil(durationMs / 60000),
          },
        });
      }
      return true;
    },
    [isFeatureEnabled, usageData, subscription, dispatchPrompt]
  );

  return downloadLimitPrompt;
};

export default useDownloadLimitPrompt;
